import { useEffect } from 'react';
import { Box } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import {
  CurrencyType,
  OrderDetailsAmountsDto,
  RefundAttemptDto,
  SettledAmountDto,
  TransactionChargesResponseDto,
} from '@tiendanube/common';
import { Domain } from '@tiendanube/common/src/enums';
import { DataList, Text } from '@tiendanube/components';
import FeatureFlag from 'App/components/FeatureFlag';
import { TOTAL_PAID_ORDER_DETAIL } from 'App/featuresFlags';
import { CurrencyPrice } from 'commons/components';
import { useBusinessData } from 'domains/Account/hooks';
import {
  useOrderDiscounts,
  useRefundCalculations,
  useShowMerchantCharges,
} from 'domains/Orders/Orders/pages/hooks';
import { OrderTotalAmountRow } from '../index';
import OrderDiscountAmountRow from '../OrderDiscountAmountRow';
import OrderRefundAmountRow from '../OrderRefundAmountRow';
import OrderSubtotalAmountRow from '../OrderSubtotalAmountRow';

interface OrderSummaryAmountsProps {
  amounts: OrderDetailsAmountsDto;
  currency: CurrencyType;
  totalProducts: number;
  paymentMethod?: string;
  refundAttempts: RefundAttemptDto[];
  charges?: TransactionChargesResponseDto;
  settledAmount?: SettledAmountDto;
}

function OrderSummaryAmounts({
  amounts,
  refundAttempts,
  currency,
  totalProducts,
  paymentMethod,
  charges,
  settledAmount,
}: OrderSummaryAmountsProps): JSX.Element {
  const { t } = useTranslation(Domain.ORDERS);
  const discounts = useOrderDiscounts(amounts, paymentMethod);

  const {
    subtotal,
    total,
    weight,
    shipping,
    shippingMethod,
    shippingDetail,
    totalPaid,
    otherCosts,
  } = amounts;

  const hasWeight = !!(weight && weight > 0);
  const shippingMethodWeight = shippingMethod || hasWeight;
  const { fetchBusinessData } = useBusinessData();
  const { hasSuccessfulRefund } = useRefundCalculations(refundAttempts);
  const { hasMerchantCharges, hasRefundedMerchantCharges } =
    useShowMerchantCharges(charges);

  const hasChargesOrRefund =
    hasSuccessfulRefund() ||
    hasMerchantCharges() ||
    hasRefundedMerchantCharges();

  useEffect(() => {
    fetchBusinessData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <DataList.Row grayed id="subtotal">
        <DataList.Cell width={70}>
          <Text>{`${t('orderSummaryCard.tableAmounts.subtotal', {
            count: totalProducts,
          })}`}</Text>
        </DataList.Cell>
        <DataList.Cell width={30}>
          <CurrencyPrice
            price={subtotal}
            currency={currency}
            block
            textAlign="right"
          />
        </DataList.Cell>
      </DataList.Row>
      {!!shipping && shipping > 0 && (
        <DataList.Row grayed id="shippingCost">
          <DataList.Cell width={70}>
            <Text>
              {shippingDetail
                ? `${t('orderSummaryCard.tableAmounts.shippingDetail', {
                    shippingDetail,
                  })}`
                : `${t('orderSummaryCard.tableAmounts.shipping', {
                    shippingMethod,
                  })}`}
            </Text>
          </DataList.Cell>
          <DataList.Cell width={30}>
            <CurrencyPrice
              price={shipping}
              currency={currency}
              block
              textAlign="right"
            />
          </DataList.Cell>
        </DataList.Row>
      )}
      {discounts.map((discount) => (
        <OrderDiscountAmountRow
          key={discount.id}
          discount={discount}
          currency={currency}
        />
      ))}
      {hasChargesOrRefund && (
        <OrderSubtotalAmountRow
          currency={currency}
          total={total}
          settledAmount={settledAmount}
        />
      )}
      {hasSuccessfulRefund() && (
        <OrderRefundAmountRow
          refundAttempts={refundAttempts}
          defaultCurrency={currency}
        />
      )}
      <OrderTotalAmountRow
        isEdited={false}
        total={total}
        refundAttempts={refundAttempts}
        currency={currency}
        merchantChargesTotalAmount={charges?.merchantChargesTotal}
        refundedMerchantChargesTotalAmount={
          charges?.refundedMerchantChargesTotal
        }
        settledAmount={settledAmount}
        orderHasSubtotal={hasChargesOrRefund}
      />
      <FeatureFlag flag={TOTAL_PAID_ORDER_DETAIL}>
        {!!totalPaid && !!otherCosts && (
          <>
            <DataList.Row id="otherCosts">
              <DataList.Cell width={70}>
                <Text>{`${t(
                  'orderSummaryCard.tableAmounts.otherCosts',
                )}`}</Text>
              </DataList.Cell>
              <DataList.Cell width={30}>
                <CurrencyPrice price={otherCosts} block textAlign="right" />
              </DataList.Cell>
            </DataList.Row>

            <DataList.Row id="totalPaid" grayed>
              <DataList.Cell width={70}>
                <Text bold>{`${t(
                  'orderSummaryCard.tableAmounts.totalPaid',
                )}`}</Text>
              </DataList.Cell>
              <DataList.Cell width={30}>
                <CurrencyPrice price={totalPaid} block textAlign="right" bold />
              </DataList.Cell>
            </DataList.Row>
          </>
        )}
      </FeatureFlag>

      {!!shippingMethodWeight && (
        <DataList.Row id="weight">
          <Box
            gap="none"
            display="flex"
            paddingLeft="2"
            flexDirection="column"
            paddingTop="1"
            paddingBottom="2"
          >
            {!!shippingMethod && <Text bold>{shippingMethod}</Text>}
            {hasWeight && (
              <Text>
                {`${t('orderSummaryCard.tableAmounts.weightTotal', {
                  weight,
                })}`}
              </Text>
            )}
          </Box>
        </DataList.Row>
      )}
    </>
  );
}
export default OrderSummaryAmounts;
